import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import {
  CreateDeepAgentPayload,
  DeepAgent,
  Message,
  MessageRequest,
  SendFeedbackPayload,
  UpdateDeepAgentPayload,
} from '../types/deep-agent.dictionary'

@Injectable({ providedIn: 'root' })
export class DeepAgentApi extends ApiHttpService {
  #agentBuilder = `agent-builder/api/v1/deep`
  #deepAgents = `agent-builder/api/v1/deep-agents`

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  // CRUD operations for Deep Agents
  createDeepAgent(payload: CreateDeepAgentPayload): Observable<DeepAgent> {
    return this.post(this.#deepAgents, payload)
  }

  getDeepAgents(): Observable<{ items: DeepAgent[]; total: number }> {
    return this.get(this.#deepAgents)
  }

  getDeepAgentsByApplication(
    applicationId: string
  ): Observable<{ items: DeepAgent[]; total: number }> {
    return this.get(`${this.#deepAgents}/filtered/${applicationId}`)
  }

  getDeepAgentById(id: string): Observable<DeepAgent> {
    return this.get(`${this.#deepAgents}/${id}`)
  }

  updateDeepAgent(id: string, payload: UpdateDeepAgentPayload): Observable<DeepAgent> {
    return this.patch(`${this.#deepAgents}/${id}`, payload)
  }

  deleteDeepAgent(id: string): Observable<{ message: string }> {
    return this.delete(`${this.#deepAgents}/${id}`)
  }

  publishDeepAgent(id: string): Observable<DeepAgent> {
    return this.post(`${this.#deepAgents}/${id}/publish`, {})
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#agentBuilder}/${payload.agentId}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  getBuildResponse(payload: MessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#agentBuilder}/build`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answer_object) => ({
        ...answer_object,
      }))
    )
  }

  getBuildWorkflowResponse(
    payload: MessageRequest
  ): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#agentBuilder}/build-workflow`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answer_object) => ({
        ...answer_object,
      }))
    )
  }

  // Test a configured deep agent
  testDeepAgent(
    agentId: string,
    payload: {
      query: string
      thread_id?: string
      action_response?: 'approve' | 'reject' | 'edit'
      action_name?: string
      feedback?: any
    }
  ): Observable<any> {
    return this.streamArray(`${this.#agentBuilder}/test/${agentId}`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)
          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answer_object) => ({
        ...answer_object,
      }))
    )
  }

  // Get available tools
  getAvailableTools(): Observable<{
    tools: Array<{
      name: string
      description: string
      source?: string
      toolkit?: string
      toolkit_display_name?: string
      category?: string
      requires_auth?: boolean
    }>
  }> {
    return this.get(`${this.#agentBuilder}/tools/available`)
  }

  // Get community toolkits list
  getCommunityToolkits(): Observable<
    Array<{
      name: string
      description: string
      category: string
      requires_auth: boolean
      auth_type: string
      tools: string[]
    }>
  > {
    return this.get(`${this.#agentBuilder}/toolkits/community`)
  }
}
